/* eslint-disable max-lines-per-function */
/* eslint-disable react/jsx-one-expression-per-line */
import React from 'react'
import type { StateMethods } from '@hookstate/core'
import { Dialog, DialogContent, IconButton, Box } from '@mui/material'
import { useLang } from '~/hooks/useLang'
import CloseIcon from '@mui/icons-material/Close'
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew'
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos'
import { Controlled as ControlledZoom } from 'react-medium-image-zoom'
import 'react-medium-image-zoom/dist/styles.css'

interface MediaItem {
  readonly mediaPath: string
  readonly fileType: string
  readonly id: string
}

interface ModalMediaCarouselProps {
  readonly open: StateMethods<boolean>
  readonly mediaItems: readonly MediaItem[]
  readonly currentIndex: StateMethods<number>
}

export default function ModalMediaCarousel({
  open,
  mediaItems,
  currentIndex,
}: ModalMediaCarouselProps) {
  const { translate } = useLang()
  const [isZoomed, setIsZoomed] = React.useState(false)
  const suppressCloseRef = React.useRef(false)
  const isUnzoomingRef = React.useRef(false)

  React.useEffect(() => {
    // kept intentionally empty after removing logs
  }, [open.get()])

  React.useEffect(() => {
    const handleGlobalClick = (event: MouseEvent) => {
      const target = event.target as Element

      if (
        target.closest('[data-rmiz-modal]') ||
        target.closest('[data-rmiz-modal-overlay]') ||
        target.closest('[data-rmiz-modal-content]') ||
        target.closest('[data-rmiz-modal-img]') ||
        target.closest('[data-rmiz-btn-unzoom]')
      ) {
        suppressCloseRef.current = true
        isUnzoomingRef.current = true
        window.setTimeout(() => {
          suppressCloseRef.current = false
          isUnzoomingRef.current = false
        }, 500)
      }
    }

    document.addEventListener('click', handleGlobalClick, true)
    document.addEventListener('mousedown', handleGlobalClick, true)

    return () => {
      document.removeEventListener('click', handleGlobalClick, true)
      document.removeEventListener('mousedown', handleGlobalClick, true)
    }
  }, [])

  const beginSuppressClose = () => {
    suppressCloseRef.current = true
    window.setTimeout(() => {
      suppressCloseRef.current = false
    }, 250)
  }

  const isZoomPortalOpen = () => Boolean(document.querySelector('[data-rmiz-modal]'))

  const handleVideoLoad = (event: React.SyntheticEvent<HTMLVideoElement>) => {
    event.currentTarget.volume = 0.2
  }

  const handleClose = () => {
    setIsZoomed(false)
    open.set(false)
  }

  const handlePrevious = () => {
    currentIndex.set((prev) => (prev > 0 ? prev - 1 : mediaItems.length - 1))
  }

  const handleNext = () => {
    currentIndex.set((prev) => (prev < mediaItems.length - 1 ? prev + 1 : 0))
  }

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (isZoomed || isZoomPortalOpen()) return

    if (event.key === 'ArrowLeft') handlePrevious()
    if (event.key === 'ArrowRight') handleNext()
    if (event.key === 'Escape') handleClose()
  }

  const handleZoomChange = (zoomed: boolean) => {
    setIsZoomed(zoomed)

    if (!zoomed && isZoomed) {
      suppressCloseRef.current = true
      isUnzoomingRef.current = true
      window.setTimeout(() => {
        suppressCloseRef.current = false
        isUnzoomingRef.current = false
      }, 600)
    }
  }

  const currentMedia = mediaItems[currentIndex.get()]
  const isVideo = currentMedia?.fileType === 'mp4'
  const isImage = ['png', 'jpeg', 'jpg'].includes(currentMedia?.fileType)

  return (
    <Dialog
      open={open.get()}
      onClose={(event, reason) => {
        if (isZoomed || isUnzoomingRef.current || isZoomPortalOpen() || suppressCloseRef.current) {
          return
        }
        handleClose()
      }}
      maxWidth="lg"
      fullWidth
      onKeyDown={handleKeyPress}
      disableEscapeKeyDown={isZoomed || isZoomPortalOpen()}
    >
      <Box
        sx={{
          position: 'absolute',
          top: 8,
          right: 8,
          zIndex: 1,
        }}
      >
        <IconButton
          onClick={handleClose}
          sx={{
            color: 'white',
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.7)' },
          }}
        >
          <CloseIcon />
        </IconButton>
      </Box>

      <DialogContent sx={{ overflow: 'hidden', p: 0, position: 'relative' }}>
        {isImage && (
          <ControlledZoom isZoomed={isZoomed} onZoomChange={handleZoomChange} zoomMargin={50}>
            <div onMouseDown={beginSuppressClose} onTouchStart={beginSuppressClose} style={{ display: 'block' }}>
              <img
                src={currentMedia.mediaPath}
                alt="Media"
                style={{
                  width: '100%',
                  height: 700,
                  objectFit: 'contain',
                  display: 'block',
                }}
              />
            </div>
          </ControlledZoom>
        )}

        {isVideo && (
          <video
            key={currentMedia.id}
            style={{ width: '100%', height: 700, display: 'block' }}
            src={currentMedia.mediaPath}
            autoPlay
            controls
            onLoadedMetadata={handleVideoLoad}
          />
        )}

        {mediaItems.length > 1 && (
          <>
            <IconButton
              onClick={handlePrevious}
              sx={{
                position: 'absolute',
                left: 16,
                top: '50%',
                transform: 'translateY(-50%)',
                color: 'white',
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.7)' },
                zIndex: 2,
              }}
            >
              <ArrowBackIosNewIcon />
            </IconButton>

            <IconButton
              onClick={handleNext}
              sx={{
                position: 'absolute',
                right: 16,
                top: '50%',
                transform: 'translateY(-50%)',
                color: 'white',
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.7)' },
                zIndex: 2,
              }}
            >
              <ArrowForwardIosIcon />
            </IconButton>

            <Box
              sx={{
                position: 'absolute',
                bottom: 16,
                left: '50%',
                transform: 'translateX(-50%)',
                color: 'white',
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                padding: '4px 12px',
                borderRadius: '16px',
                zIndex: 2,
              }}
            >
              {currentIndex.get() + 1} /{mediaItems.length}
            </Box>
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}
